"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        <div className="h-6 w-20 bg-muted/10 rounded-md" />
        <div className="h-6 w-20 bg-muted/10 rounded-md" />
        <div className="h-6 w-20 bg-muted/10 rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent font-default text-foreground/70 transition-all duration-300 hover:text-primary text-sm font-semibold tracking-wider uppercase p-0 h-auto gap-1">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-[220px] bg-card/95 backdrop-blur-xl border border-border/40 p-2 flex flex-col gap-1">
                  {item.children.map(child => <NavigationMenuLink key={child.id} asChild>
                      <Link to={child.href || "#"} className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent/10 rounded-md transition-colors">
                        {child.label}
                      </Link>
                    </NavigationMenuLink>)}
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="font-default text-foreground/70 transition-all duration-300 hover:text-primary hover:underline decoration-primary underline-offset-8 decoration-2 text-sm font-semibold tracking-wider uppercase">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-card border-b border-border/40 p-6 flex flex-col gap-6 md:hidden shadow-2xl origin-top animate-in slide-in-from-top-2 fade-in duration-200 backdrop-blur-xl max-h-[80vh] overflow-y-auto">
      {loading ? <div className="flex flex-col gap-4">
          <div className="h-8 w-32 bg-muted/10 rounded-md" />
          <div className="h-8 w-32 bg-muted/10 rounded-md" />
        </div> : <nav className="flex flex-col gap-2">
          {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItems[item.id];
        return <div key={item.id} data-index={i} className="flex flex-col">
                <div className="flex items-center justify-between border-b border-border/10">
                  <Link to={item.href || "#"} className="font-default text-foreground hover:text-primary text-lg font-bold py-3 transition-colors tracking-tight flex-grow" onClick={() => !hasChildren && setIsOpen(false)}>
                    {item.label}
                  </Link>
                  {hasChildren && <Button variant="ghost" size="icon" className="h-12 w-12 text-foreground/50 hover:bg-accent/10 rounded-md" onClick={() => toggleExpand(item.id)}>
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    </Button>}
                </div>
                {hasChildren && isExpanded && <div className="flex flex-col pl-4 bg-accent/5 rounded-md mt-1 animate-in fade-in slide-in-from-top-1 duration-200">
                    {item.children?.map(child => <Link key={child.id} to={child.href || "#"} className="font-default text-foreground/70 hover:text-primary text-base font-medium py-3 border-b border-border/5 last:border-0 transition-colors" onClick={() => setIsOpen(false)}>
                        {child.label}
                      </Link>)}
                  </div>}
              </div>;
      })}
        </nav>}
      <Link to="/GetAQuotePage" onClick={() => setIsOpen(false)} className="mt-4">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-bold shadow-lg shadow-primary/30 rounded-md">
          Get a Quote
        </Button>
      </Link>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="259823" id="header" className="sticky top-0 z-50 w-full transition-all duration-500 ease-in-out text-foreground bg-background/80 backdrop-blur-[12px] border-b border-border/40 shadow-md dark">
      <div className="container mx-auto px-4 md:px-6 h-20 md:h-24 flex items-center justify-between">
        <Link to="/HomePage" className="flex-shrink-0 transition-transform hover:scale-105 duration-300">
          <WvcLogo className="h-10 text-primary !w-[50px] md:h-14 [&_svg]:h-10 [&_svg]:text-primary [&_svg]:!w-[50px] [&_svg]:md:h-14" />
        </Link>

        <MenuProvider menu_id="46">
          <DesktopMenu />
        </MenuProvider>

        <div className="flex items-center gap-4">
          <Link to="/GetAQuotePage" className="hidden md:block">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/40 font-bold px-8 rounded-md tracking-tight">
              Get a Quote
            </Button>
          </Link>

          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/20 rounded-md" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      <MenuProvider menu_id="46">
        <MobileMenu isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </section>;
}